import type {ClassValue} from 'clsx';
import clsx from 'clsx';
import {defineQuery} from 'next-sanity';
import {twMerge} from 'tailwind-merge';

import {DOCUMENT_TYPE_SIMPLEVIEW_EVENT_CALENDAR_FRONTPAGE} from '../components/simpleview-event-calendar/constants';
import {
	DOCUMENT_TYPE_ARTICLE,
	DOCUMENT_TYPE_CATEGORY,
	DOCUMENT_TYPE_FRONTPAGE,
	DOCUMENT_TYPE_PAGE,
} from '../schema/documents/constants';
import type {LinkableDocumentTypes} from '../schema/primitives/link';

/**
 * Splits a string into groups of 2 characters and joins them with a space
 * Gets rid of all non-digit characters, in case there are already spaces in the string
 * @param value - The string to split
 * @returns The string with spaces between the groups
 * @example
 * phoneNumber('1234567') // 12 34 56 7
 * phoneNumber('123 4567') // 12 34 56 7
 */
export const formatPhoneNumber = (value: string) => {
	const groups = value.replace(/\D/g, '').match(/.{1,2}/g);
	return groups?.join(' ') ?? '';
};

export const dateToWeekday = (date?: string, locale: string = 'en') => {
	if (!date) {
		return '';
	}

	const dateObj = new Date(date);
	const weekday = dateObj
		.toLocaleString(locale, {weekday: 'long'})
		.replace(/^\w/, (c) => c.toUpperCase());
	return weekday;
};

export const formatDate = (date?: string) => {
	if (!date) {
		return '';
	}

	const dateObj = new Date(date);
	const day = dateObj.getDate().toString().padStart(2, '0');
	const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
	const year = dateObj.getFullYear().toString().slice(2);

	return `${day}.${month}.${year}`;
};

export const documentToHref = (
	categorySlug: string | undefined,
	documentSlug: string | undefined,
	type: LinkableDocumentTypes,
) => {
	if (!type) {
		return '#';
	}

	//const categorySlug = document.category?.slug?.current;
	//const documentSlug = document.slug?.current;
	switch (type) {
		case 'category':
			return `/${documentSlug ?? ''}`;
		case 'frontpage':
			return '/';
		case 'page':
			return categorySlug
				? `/${categorySlug}/${documentSlug ?? ''}`
				: `/${documentSlug ?? ''}`;
		case 'article':
			return categorySlug
				? `/${categorySlug}/artikler/${documentSlug ?? ''}`
				: `/artikler/${documentSlug ?? ''}`;
		case 'simpleviewEventCalendarFrontpage':
			return '/kalender';
	}

	return '#';
};

/**
 * Fetches the documents referenced by internal links. Takes a list of ids so
 * that all the links on a page can be resolved in one request, see
 * `components/link/resolve.ts`.
 */
export const linkReferencesByIdsQuery = defineQuery(`
	*[(_type == "${DOCUMENT_TYPE_PAGE}" || _type == "${DOCUMENT_TYPE_FRONTPAGE}" || _type == "${DOCUMENT_TYPE_CATEGORY}" || _type == "${DOCUMENT_TYPE_ARTICLE}" || _type == "${DOCUMENT_TYPE_SIMPLEVIEW_EVENT_CALENDAR_FRONTPAGE}") && _id in $ids]{
		_id,
		_type,
		title,
		slug,
		tagline,
		media,
		category->{
			title,
			slug,
		},
		heading,
		meta
	}
`);

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}
